import { useEffect } from 'react';

import { useQuery } from '@tanstack/react-query';
import { Loader2, TriangleAlert } from 'lucide-react';
import { Controller } from 'react-hook-form';

import type { ClientId } from '@nesso/ledger-contracts/ids';
import { Alert, AlertDescription } from '@nesso/ui/primitives/alert';
import { Button } from '@nesso/ui/primitives/button';
import { CurrencyInputField } from '@nesso/ui/primitives/currency-input-field';
import { DialogFooter } from '@nesso/ui/primitives/dialog';
import { FieldError } from '@nesso/ui/primitives/field';
import { SelectField } from '@nesso/ui/primitives/select-field';

import { formatServiceLabel, serviceCatalogOptions } from '@/entities/service-catalog';
import type { SessionCharge } from '@/entities/session-charge';

import { useEditSessionFeeForm } from '../../model/use-edit-session-fee-form';

type Props = {
    clientId: ClientId;
    sessionCharge: SessionCharge;
    onClose: () => void;
    onPendingChange: (pending: boolean) => void;
};

export function EditSessionFeeDialogForm({
    clientId,
    sessionCharge,
    onClose,
    onPendingChange,
}: Props) {
    const { data: catalog } = useQuery(serviceCatalogOptions());
    const editSessionFee = useEditSessionFeeForm({ clientId, sessionCharge, onSuccess: onClose });
    const { control, formState } = editSessionFee.form;

    useEffect(() => {
        onPendingChange(editSessionFee.isPending);
        return () => onPendingChange(false);
    }, [editSessionFee.isPending, onPendingChange]);

    const services = catalog?.services ?? [];
    // The session's own service always stays selectable, even once the practice removes it from
    // the catalog — listed first, under its code alone.
    const hasCurrentService = services.some(
        (service) => service.code === sessionCharge.serviceCode,
    );
    const options = [
        ...(hasCurrentService
            ? []
            : [
                  {
                      value: sessionCharge.serviceCode,
                      label: formatServiceLabel(sessionCharge.serviceCode, undefined),
                  },
              ]),
        ...services.map((service) => ({
            value: service.code,
            label: formatServiceLabel(service.code, service.description),
        })),
    ];

    return (
        <form onSubmit={editSessionFee.onSubmit} className="flex flex-col gap-6">
            <div className="flex flex-col gap-4">
                {sessionCharge.status === 'PAID' && (
                    <Alert variant="warning">
                        <TriangleAlert />
                        <AlertDescription>
                            This session has already been paid. A higher fee will create a balance
                            due; a lower fee will create a client credit.
                        </AlertDescription>
                    </Alert>
                )}
                <Controller
                    control={control}
                    name="serviceCode"
                    render={({ field }) => (
                        <SelectField
                            id="edit-session-fee-service"
                            label="Service"
                            options={options}
                            value={field.value}
                            disabled={editSessionFee.isPending}
                            onChange={(code) => {
                                const service = services.find(
                                    (candidate) => candidate.code === code,
                                );
                                editSessionFee.selectService(
                                    code,
                                    service?.rateCents ?? sessionCharge.feeCents,
                                );
                            }}
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="fee"
                    render={({ field, fieldState }) => (
                        <CurrencyInputField
                            {...field}
                            id="edit-session-fee-fee"
                            label="Fee"
                            error={fieldState.error?.message ?? fieldState.invalid}
                            disabled={editSessionFee.isPending}
                            className="text-right"
                            inputGroupClassName="w-25"
                        />
                    )}
                />
                {formState.errors.root && <FieldError>{formState.errors.root.message}</FieldError>}
            </div>
            <DialogFooter>
                <Button
                    type="button"
                    variant="outline"
                    disabled={editSessionFee.isPending}
                    onClick={onClose}
                >
                    Cancel
                </Button>
                <Button type="submit" disabled={editSessionFee.isPending}>
                    {editSessionFee.isPending && <Loader2 className="animate-spin" />}
                    Save
                </Button>
            </DialogFooter>
        </form>
    );
}
